import { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import moment from "jalali-moment";
import { Button, Spin } from "antd";
import {
  getExperimentParams,
  getExperimentsReportResults,
} from "@services/api";
import SoilMap from "@/components/reports/soil/soil-map/SoilMap";
import {
  academicDegreeDetector,
  calculateArea,
  isShowProposition3UiHelper,
  isShowProposition4UiHelper,
  proposition2UiHelper,
  proposition3UiHelper,
  proposition4UiHelper,
  soilSampleType,
} from "@/components/reports/plant/leafHelper";
import { FieldMarkerIcon } from "@/components/map/utils/customs";
import { relativeToTheTotalArea } from "@/components/reports/soil/soilMapHelper";
import { hejriDate } from "@helpers/financial_helper";
import AgroMapFarm from "@/features/map/presentation/components/AgroMapFarm";
import { toolbarBaseControls } from "@/features/map/utils/configs";
import line from "@assets/images/leaf_map_report_images/Line185.png";
import footerImage from "@assets/images/leaf_map_report_images/footer-page.png";
import headerImage from "@assets/images/leaf_map_report_images/header-cover.png";
import frame2182 from "@assets/images/leaf_map_report_images/header-page-leaf-map.png";
import frame2173 from "@assets/images/leaf_map_report_images/Vision statement-amico 3.png";
import agroSoilMapIcon from "@assets/images/leaf_map_report_images/agroyaarSoilMapReport.svg";

function PlantReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { returnTo } = location.state;

  const soilMapReportQuery = useQuery({
    queryKey: ["soilExperimentReportQuery", id],
    queryFn: () => getExperimentsReportResults(id),
  });

  const experimentsParametersQuery = useQuery({
    queryKey: ["experimentsParametersQuery"],
    queryFn: () => getExperimentParams(),
  });

  let pageNumber = 0;
  let sampleLength;
  if (soilMapReportQuery?.data?.experiment?.stations) {
    sampleLength =
      soilMapReportQuery?.data?.experiment?.stations[0]?.samples?.length;
  }

  soilMapReportQuery?.data?.experiment?.stations?.forEach((station) => {
    if (station?.samples?.length > sampleLength) {
      sampleLength = station?.samples?.length;
    }
  });

  const parametersMap = useMemo(
    () =>
      soilMapReportQuery?.data?.experiment?.parametersMaps &&
      experimentsParametersQuery?.data?.experimentsParameters
        ? soilMapReportQuery.data.experiment.parametersMaps.map(
            (parameterMap) => {
              const parameter =
                experimentsParametersQuery.data.experimentsParameters.find(
                  (parameter) => parameter.uuid === parameterMap.parameterId
                );
              return {
                persianName: parameter?.persianName,
                name: parameter?.name,
                unit: parameter?.unit,
                mapInfo: parameterMap,
              };
            }
          )
        : [],
    [soilMapReportQuery?.data, experimentsParametersQuery?.data]
  );

  return (
    <>
      {soilMapReportQuery?.isLoading ? (
        <div className="flex flex-row items-center w-[500px] h-[500px] mx-auto my-[6rem]">
          <Spin
            className="spiner-loading-soil"
            style={{ margin: "auto" }}
            size="large"
          />
        </div>
      ) : (
        <div className="section-container flex flex-col items-center justify-center w-[720px] mx-auto !bg-white">
          {/* Page1 (Introduction) */}
          <div className="page1">
            <div className="flex flex-col justify-between items-center w-full h-[1048px]">
              <img
                className="header"
                src={headerImage}
                alt="header"
                width="720px"
              />

              <div className="flex flex-col justify-between items-center w-full mt-[-90px] leading-[4.25rem]">
                <p className="text-[20px] font-semibold text-center">
                  {" "}
                  شبكه آزمايشگاه هاي خوشه پروران ( اگرويار)
                </p>

                <p
                  className="text-[#222] text-[36px] font-bold !font-[B-Nazanin]"
                  style={{ textShadow: "0px 2px 4px rgba(0, 0, 0, 0.25)" }}
                >
                  کشاورزی{" "}
                  <span className="text-lab !font-[B-Nazanin]">
                    مطمئن‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر، </span>
                  </span>
                  <span className="text-lab !font-[B-Nazanin]">
                    دقیق‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر و </span>
                  </span>
                  <span className="text-lab !font-[B-Nazanin]">
                    اقتصادی‌
                    <span className="text-[#222] !font-[B-Nazanin]">تر</span>
                  </span>
                </p>

                <img
                  src={frame2173}
                  className="w-[420px] !font-[B-Nazanin]"
                  alt="chart-icon"
                />

                <span
                  className="text-lab font-bold text-[64px] !font-[B-Nazanin]"
                  style={{
                    textShadow: "1px 0px 3px rgba(118, 118, 118, 0.61)",
                  }}
                >
                  گزارش نقشه عناصر برگ
                </span>

                <p className="text-[#000] font-semibold text-[24px] !font-[B-Nazanin]">
                  هدف ما تصمیم‌گیری برای کشاورز نیست، کمک به تصمیم‌گیری بهتر
                  کشاورز است
                </p>

                <p className="text-lab font-bold text-[28px] tracking-[4px]">
                  WWW.AGROYAAR.COM
                </p>
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="720px"
                  alt="footer"
                />
                <span className="absolute bottom-[5px] left-[35px] tracking-[7px] z-[3] text-[14px] font-bold text-[#fff] !font-sans">
                  WWW.AGROYAAR.COM
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </div>
          </div>

          {/* Page2 (Description) */}
          <div className="page2">
            <section className="flex flex-col justify-between items-center w-full h-[1048px]">
              <div className="header w-full relative">
                <img
                  className="header"
                  src={frame2182}
                  alt="header"
                  width="720px"
                />
                <span className="absolute top-1 left-[20px] z-[3] text-[14px] font-bold text-[#fff] tracking-[7px] text-center">
                  WWW.AGROYAAR.COM
                </span>
              </div>

              <div className="flex flex-row justify-start items-center w-full gap-5 mt-[-30px] px-4">
                <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                  <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                    اطلاعات کشاورز
                  </span>

                  <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                    <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                      <span className="!font-[B-Nazanin]">
                        {
                          soilMapReportQuery?.data?.experiment?.farmer
                            ?.firstName
                        }{" "}
                        {soilMapReportQuery?.data?.experiment?.farmer?.lastName}
                      </span>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">کد کشاورزی:</span>
                        <span className="!font-sans text-[16px]">
                          {
                            soilMapReportQuery?.data?.experiment?.farmer
                              ?.farmerCode
                          }
                        </span>
                      </div>

                      <div className="flex flex-row items-start gap-2">
                        <span className="!font-[B-Nazanin]">استان و شهر:</span>
                        <span className="!font-[B-Nazanin]">
                          {
                            soilMapReportQuery?.data?.experiment?.farmer
                              ?.province
                          }
                          ، {soilMapReportQuery?.data?.experiment?.farmer?.city}
                        </span>
                      </div>
                    </div>

                    <img
                      src={
                        process.env.REACT_APP_ARVAN_S3_BASE_URL +
                        soilMapReportQuery?.data?.experiment?.farmer?.avatar
                      }
                      alt="profile"
                      width="70px"
                      height="70px"
                      className="rounded-full border-[2px] border-solid border-lab"
                    />
                  </div>
                </div>

                {soilMapReportQuery?.data?.experiment?.expert && (
                  <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                    <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                      اطلاعات کارشناس
                    </span>

                    <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                      <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                        <span className="!font-[B-Nazanin]">
                          {
                            soilMapReportQuery?.data?.experiment?.expert
                              ?.firstName
                          }{" "}
                          {
                            soilMapReportQuery?.data?.experiment?.expert
                              ?.lastName
                          }
                        </span>

                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">
                            کد کارشناسی:
                          </span>
                          <span className="!font-sans text-[16px]">
                            {
                              soilMapReportQuery?.data?.experiment?.expert
                                ?.expertCode
                            }
                          </span>
                        </div>

                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">
                            استان و شهر:
                          </span>
                          <span className="!font-[B-Nazanin]">
                            {
                              soilMapReportQuery?.data?.experiment?.expert
                                ?.province
                            }
                            ،{" "}
                            {soilMapReportQuery?.data?.experiment?.expert?.city}
                          </span>
                        </div>
                      </div>

                      <img
                        src={
                          process.env.REACT_APP_ARVAN_S3_BASE_URL +
                          soilMapReportQuery?.data?.experiment?.expert?.avatar
                        }
                        alt="profile"
                        width="70px"
                        height="70px"
                        className="rounded-full border-[2px] border-solid border-lab"
                      />
                    </div>
                  </div>
                )}
              </div>

              <img
                src={line}
                alt="line"
                width="720px"
                className="mt-[-50px] mr-0 mb-[-30px] ml-0"
              />

              <div className="flex flex-col justify-start items-start w-full gap-5 px-4">
                <div className="flex flex-row items-center justify-center w-full gap-6">
                  <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                    <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                      اطلاعات زمین
                    </span>
                    <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                      <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">نام قطعه:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              soilMapReportQuery?.data?.experiment?.season?.farm
                                ?.name
                            }
                          </span>
                        </div>
                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">استان:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              soilMapReportQuery?.data?.experiment?.season?.farm
                                ?.province?.name
                            }
                          </span>
                        </div>
                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">شهر:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              soilMapReportQuery?.data?.experiment?.season?.farm
                                ?.city?.name
                            }
                          </span>
                        </div>
                      </div>

                      <div className="relative w-[80px] h-[80px] overflow-clip rounded-[999px]">
                        <div className="THISISFORMAPLOCK! bg-transparent z-[99999999] w-full h-full absolute"></div>
                        <AgroMapFarm
                          mapId={
                            soilMapReportQuery?.data?.experiment?.season?.farm
                              ?.id
                          }
                          type="onlyShow"
                          toolbarOptions={toolbarBaseControls}
                          markerIcon={FieldMarkerIcon}
                          currentFarmPolygon={
                            soilMapReportQuery?.data?.experiment?.season?.farm
                              ?.polygon
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col justify-center items-start w-[50%] gap-2">
                    <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                      اطلاعات فصل رشدی
                    </span>

                    <div className="flex flex-row-reverse justify-end items-center gap-[19px] w-full">
                      <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]"> نام محصول:</span>
                          <span className="!font-[B-Nazanin]">
                            {
                              soilMapReportQuery?.data?.experiment?.season
                                ?.product?.persianName
                            }
                          </span>
                        </div>
                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">مساحت زمین:</span>
                          <span className="!font-[B-Nazanin]">
                            {soilMapReportQuery?.data?.experiment?.farm?.area}{" "}
                            هکتار
                          </span>
                        </div>

                        <div className="flex flex-row items-start gap-2">
                          <span className="!font-[B-Nazanin]">فصل رشد:</span>
                          <span className="!font-[B-Nazanin]">
                            {moment(
                              `${soilMapReportQuery?.data?.experiment?.season.growingSeason}-04-01`,
                              "YYYY-MM-DD"
                            )
                              .locale("fa")
                              .format("YYYY")}{" "}
                            {soilMapReportQuery?.data?.experiment?.season
                              .persianName
                              ? ` - ${soilMapReportQuery?.data?.experiment?.season.persianName}`
                              : ""}
                          </span>
                        </div>
                      </div>
                      <div className="relative w-[80px] h-[80px] overflow-clip rounded-[999px]">
                        <div className="THISISFORMAPLOCK! bg-transparent z-[99999999] w-full h-full absolute"></div>
                        <AgroMapFarm
                          mapId={
                            soilMapReportQuery?.data?.experiment?.season?.id
                          }
                          type="onlyShow"
                          toolbarOptions={toolbarBaseControls}
                          markerIcon={FieldMarkerIcon}
                          currentFarmPolygon={
                            soilMapReportQuery?.data?.experiment?.season
                              ?.polygon
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <img
                src={line}
                alt="line"
                width="720px"
                className="mt-[-50px] mr-0 mb-[-30px] ml-0"
              />

              <div className="flex flex-col items-start justify-center w-full leading-[2rem] px-4 gap-2">
                <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                  اطلاعات آزمایش
                </span>

                <div className="flex flex-row items-start justify-start w-full gap-6">
                  <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                    <div className="flex flex-row items-start gap-2">
                      <span className="!font-[B-Nazanin]">نوع نمونه:</span>
                      <span className="!font-[B-Nazanin]">
                        {soilSampleType(
                          soilMapReportQuery?.data?.experiment?.category
                            ?.categoryFA
                        )}
                      </span>
                    </div>

                    <div className="flex flex-row items-start gap-2">
                      <span className="!font-[B-Nazanin]">
                        تاریخ نمونه‌برداری:
                      </span>
                      <span className="!font-[B-Nazanin]">
                        {hejriDate(
                          soilMapReportQuery?.data?.experiment?.samplingAt
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                    <div className="flex flex-row items-start gap-2">
                      <span className="!font-[B-Nazanin]">
                        منطقه گیاهی نمونه:
                      </span>
                      <span className="!font-[B-Nazanin]">
                        برگ‌های جوان توسعه‌یافته
                      </span>
                      {/* <span className='!font-[B-Nazanin]'>{samplingDepth(sampleLength)}</span> */}
                    </div>

                    <div className="flex flex-row items-start gap-2">
                      <span className="!font-[B-Nazanin]">
                        تاریخ دریافت نمونه:
                      </span>
                      <span className="!font-[B-Nazanin]">
                        {hejriDate(
                          soilMapReportQuery?.data?.experiment?.receiptAt
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="text-agrotext font-bold text-[18px] flex flex-col justify-center items-start gap-[6px]">
                    <div className="flex flex-row items-start gap-2">
                      <span className="!font-[B-Nazanin]">تعداد نمونه:</span>
                      <span className="!font-[B-Nazanin]">
                        {soilMapReportQuery?.data?.experiment?.results?.length}
                      </span>
                    </div>

                    <div className="flex flex-row items-start gap-2">
                      <span className="!font-[B-Nazanin] tracking-tight">
                        تاریخ آنالیز نمونه:
                      </span>
                      <span className="!font-[B-Nazanin]">
                        {hejriDate(
                          soilMapReportQuery?.data?.experiment?.analysisAt
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <img
                src={line}
                alt="line"
                width="750px"
                className="mt-[-50px] mr-0 mb-[-30px] ml-0"
              />

              <div className="flex flex-col justify-center items-start w-full gap-1 mb-[6.5rem] px-4">
                <span className="font-bold text-[20px] text-[#222] !font-[B-Nazanin]">
                  محل و نقاط نمونه برداری
                </span>

                <div className="w-[680px] h-[310px] rounded-lg bg-slate-200">
                  <div className="THISISFORMAPLOCK! bg-transparent z-[99999999] w-[680px] h-[310px] absolute"></div>

                  {/* <Map
                    service={Service.FieldView}
                    polygonCoordinates={
                      soilMapReportQuery?.data?.experiment?.farm?.polygon
                    }
                    index="15"
                    productId={
                      soilMapReportQuery?.data?.experiment?.farm?.productId
                    }
                    samplingStationPoints={
                      soilMapReportQuery?.data?.experiment?.stations
                    }
                    serviceReport={"SOIL"}
                  /> */}

                  <AgroMapFarm
                    mapId={`222${soilMapReportQuery?.data?.experiment?.season?.polygon}`}
                    type="onlyShow"
                    toolbarOptions={toolbarBaseControls}
                    markerIcon={FieldMarkerIcon}
                    currentFarmPolygon={
                      soilMapReportQuery?.data?.experiment?.season?.polygon
                    }
                    samplingStationPoints={
                      soilMapReportQuery?.data?.experiment?.stations
                    }
                    mapForWhatReport="EXPERIMENT"
                  />
                </div>
              </div>

              <div className="footer w-full flex flex-col justify-start items-center relative">
                <img
                  className="absolute bottom-0 z-[2]"
                  src={footerImage}
                  width="750px"
                  alt="footer"
                />
                <span className="absolute bottom-[5px] left-[35px] z-[3] text-[14px] font-bold text-[#fff] !font-sans">
                  کد آزمایش:{" "}
                  {soilMapReportQuery?.data?.experiment?.experimentCode}
                </span>
                <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                  {++pageNumber}
                </span>
              </div>
            </section>
          </div>

          {/* Page3 (generating dynamic pages) */}
          {parametersMap.map((parameterMapInfo, i) => {
            return (
              <div key={i} className="page3">
                <div className="flex flex-col justify-between items-start w-full h-[1048px]">
                  <img
                    className="header"
                    src={frame2182}
                    alt="header"
                    width="720px"
                  />

                  <div className="flex flex-col items-center justify-start w-full h-full gap-4 px-4">
                    {i === 0 && (
                      <div className="flex flex-col items-start justify-center w-full gap-1 mt-[-10px]">
                        <p className="font-bold text-[18px] text-right text-[#222] !font-[B-Nazanin]">
                          نقشه عناصر برگ
                        </p>

                        <p className="font-normal text-[16px] text-right text-[#222] !font-[B-Nazanin]">
                          نقشه عناصر برگ وضعیت و مقدار هر کدام از عناصر و
                          پارامترهای برگ را در محدوده زمین شما به تصویر می‌کشد.
                          به طوری که میتوانید توزیع عناصر برگ را در بخش‌های
                          مختلف زمین مورد قیاس قرار داده و تصمیم‌گیری بهتری در
                          جهت بهبود وضعیت گیاه انجام دهید.
                        </p>
                      </div>
                    )}

                    <div className="flex flex-col items-start justify-center w-full">
                      <p className="font-bold text-[18px] text-right text-lab !font-[B-Nazanin]">
                        {parameterMapInfo?.persianName} {parameterMapInfo?.name}
                      </p>

                      <p className="font-bold text-[18px] text-right text-[#222] !font-[B-Nazanin]">
                        نقشه مقدار {parameterMapInfo?.persianName} برگ یا{" "}
                        <span dir="ltr">
                          {parameterMapInfo?.unit[0] === "(" ||
                          !parameterMapInfo?.unit
                            ? parameterMapInfo?.unit
                            : `(${parameterMapInfo?.unit})`}{" "}
                          {parameterMapInfo?.name}
                        </span>
                      </p>

                      <div className="flex flex-col items-center justify-center w-full border-[3px] border-solid bg-lab border-lab rounded-[10px]">
                        <div className="relative w-[685px] h-[438px] overflow-clip border-b-[3px] border-solid border-lab rounded-tr-[7px] rounded-tl-[7px]">
                          <div className="flex flex-row items-start justify-between w-[670px] absolute z-[9999] right-2 top-2">
                            <div className="bg-white !font-[B-Nazanin] font-bold rounded-[15px] min-w-[132px] text-sm py-1 text-center border-[1.5px] border-solid border-lab">
                              قطعه{" "}
                              {soilMapReportQuery?.data?.experiment?.farm?.name}
                            </div>

                            <img
                              src={agroSoilMapIcon}
                              width={200}
                              height={30}
                              alt="agroSoilMapIcon"
                            />
                          </div>

                          <div className="THISISFORMAPLOCK! bg-transparent z-[999] w-full h-full absolute"></div>

                          <SoilMap
                            parameterMapInfo={parameterMapInfo}
                            polygonBounds={
                              soilMapReportQuery?.data?.experiment?.season
                                ?.polygon
                            }
                            mapId={
                              parameterMapInfo?.mapInfo?.parameterId +
                              parameterMapInfo?.mapInfo?.samplingDepthId
                            }
                          />
                        </div>

                        <div className="flex flex-row items-start rounded-br-[7px] rounded-bl-[7px] bg-gradient-to-l from-[#ffffffcc] to-white justify-between w-full px-2">
                          <div className="flex flex-col items-start">
                            <p className="!font-[B-Nazanin] font-bold">
                              کشاورز:{" "}
                              {
                                soilMapReportQuery?.data?.experiment?.farmer
                                  ?.firstName
                              }{" "}
                              {
                                soilMapReportQuery?.data?.experiment?.farmer
                                  ?.lastName
                              }
                            </p>
                            {soilMapReportQuery?.data?.experiment?.expert && (
                              <p className="!font-[B-Nazanin] font-bold">
                                کارشناس:{" "}
                                {academicDegreeDetector(
                                  soilMapReportQuery?.data?.experiment?.expert
                                    ?.academicDegreeId
                                )}{" "}
                                {
                                  soilMapReportQuery?.data?.experiment?.expert
                                    ?.firstName
                                }{" "}
                                {
                                  soilMapReportQuery?.data?.experiment?.expert
                                    ?.lastName
                                }
                              </p>
                            )}
                            <p className="!font-[B-Nazanin] font-bold">
                              نوع آزمایش:{" "}
                              {soilSampleType(
                                soilMapReportQuery?.data?.experiment?.category
                                  ?.categoryFA
                              )}
                            </p>
                            <p className="!font-[B-Nazanin] font-bold">
                              مساحت:{" "}
                              {
                                soilMapReportQuery?.data?.experiment?.season
                                  ?.area
                              }{" "}
                              هکتار
                            </p>
                            <p className="!font-[B-Nazanin] font-bold">
                              محل زمین:{" "}
                              {
                                soilMapReportQuery?.data?.experiment?.season
                                  ?.farm?.province?.name
                              }
                              ,{" "}
                              {
                                soilMapReportQuery?.data?.experiment?.season
                                  ?.farm?.city?.name
                              }
                            </p>
                          </div>

                          <table
                            dir="ltr"
                            style={{ borderCollapse: "collapse" }}
                          >
                            <thead>
                              <tr className="border-b-[1px] border-solid border-lab">
                                <th
                                  dir="rtl"
                                  className="w-[80px] !font-[B-Nazanin] text-[16px] text-center"
                                >
                                  محدوده
                                </th>
                                <th
                                  dir="rtl"
                                  className="w-[120px] !font-[B-Nazanin] text-[16px] text-center"
                                >
                                  مقدار {parameterMapInfo?.unit}
                                </th>
                                <th className="w-[80px] !font-[B-Nazanin] text-[16px] text-center">
                                  مساحت (هکتار)
                                </th>
                                <th
                                  dir="rtl"
                                  className="w-[150px] !font-[B-Nazanin] text-[16px] text-center"
                                >
                                  نسبت به کل مساحت (%)
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              {parameterMapInfo?.mapInfo?.ranges?.map(
                                (range, i) => {
                                  return (
                                    <tr
                                      className="border-b-[1px] border-solid border-lab"
                                      key={i}
                                    >
                                      <td className="w-[80px] text-center !font-[B-Nazanin] text-[16px] px-2 font-medium">
                                        <div className="flex flex-row items-center justify-center gap-1">
                                          <span>{range?.label}</span>
                                          <div
                                            style={{
                                              backgroundColor: `rgba(${range?.color[0]},${range?.color[1]},${range?.color[2]},${range?.color[3]})`,
                                              width: "50px",
                                              height: "20px",
                                            }}
                                          ></div>
                                        </div>
                                      </td>
                                      <td className="w-[120px] text-center !font-[B-Nazanin] text-[17px] px-2 font-medium">
                                        {range?.min} - {range?.max}
                                      </td>
                                      <td className="w-[80px] text-center !font-[B-Nazanin] text-[17px] px-2 font-medium">
                                        {calculateArea(
                                          parameterMapInfo?.mapInfo?.percentage[
                                            i
                                          ],
                                          soilMapReportQuery?.data?.experiment
                                            ?.farm?.area
                                        ) === 0
                                          ? "≈0"
                                          : calculateArea(
                                              parameterMapInfo?.mapInfo
                                                ?.percentage[i],
                                              soilMapReportQuery?.data
                                                ?.experiment?.season?.area
                                            )}
                                      </td>
                                      <td className="w-[150px] text-center !font-[B-Nazanin] text-[17px] px-2 font-medium">
                                        {relativeToTheTotalArea(
                                          parameterMapInfo?.mapInfo?.percentage[
                                            i
                                          ],
                                          parameterMapInfo?.mapInfo
                                        )}
                                      </td>
                                    </tr>
                                  );
                                }
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-start justify-start w-full">
                      <p className="font-normal text-[16px] text-right text-[#222] !font-[B-Nazanin]">
                        {proposition2UiHelper(
                          parameterMapInfo?.persianName,
                          parameterMapInfo?.unit,
                          parameterMapInfo
                        )}
                      </p>
                      {parameterMapInfo?.mapInfo?.ranges?.length > 1 &&
                        isShowProposition3UiHelper(parameterMapInfo) && (
                          <p className="font-normal text-[16px] text-right text-[#222] !font-[B-Nazanin]">
                            {proposition3UiHelper(
                              parameterMapInfo?.persianName,
                              parameterMapInfo?.unit,
                              parameterMapInfo
                            )}
                          </p>
                        )}
                      {isShowProposition4UiHelper(parameterMapInfo) && (
                        <p className="font-normal text-[16px] text-right text-[#222] !font-[B-Nazanin]">
                          {proposition4UiHelper(
                            parameterMapInfo?.persianName,
                            parameterMapInfo?.unit,
                            parameterMapInfo
                          )}
                        </p>
                      )}
                      {parameterMapInfo?.mapInfo?.description && (
                        <p className="font-normal text-[16px] text-right text-[#222] !font-[B-Nazanin]">
                          {parameterMapInfo?.mapInfo?.description}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="footer w-full flex flex-col justify-start items-center relative bottom-0">
                    <img
                      className="absolute bottom-0 z-[2]"
                      src={footerImage}
                      width="750px"
                      alt="footer"
                    />
                    <span className="absolute bottom-[5px] left-[30px] z-[3] text-[12px] font-bold text-[#fff] !font-sans">
                      کد آزمایش:{" "}
                      {soilMapReportQuery?.data?.experiment?.experimentCode}
                    </span>
                    <span className="absolute bottom-[5px] right-[15px] z-[3] text-[18px] font-semibold text-[#fff]">
                      {++pageNumber}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="flex flex-col">
            <Button
              className="button-neo h-fit !text-[#333] hover:brightness-105 fixed top-[20px] right-[100px] max-lg:right-[32px] px-8 text-[18px]  w-[150px]"
              id="download-button"
              onClick={() => window.print()}
            >
              <p>پرینت گزارش</p>
            </Button>
            <Button
              className="button-neo h-fit !text-[#333] hover:brightness-105 fixed top-[65px] right-[100px] max-lg:right-[32px] px-8 text-[18px] w-[150px]"
              id="download-button"
              onClick={() => navigate(returnTo)}
            >
              <p> بازگشت</p>
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default PlantReport;
